import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CTAButton, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatUSD } from '../shared/format';

/**
 * Module 5 — Biggest gap + action templates.
 *
 * Renders bonus_action_list_jsonb (templates Phase 1, hardcoded server-side).
 * If empty, falls back to a generic "View action plans" CTA. Each item has a
 * "Translate into action" button that opens /tcn/me/actions?source=bonus_gap
 * with the gap component pre-filled.
 *
 * Props:
 *  - component: string ('rankup' | 'activeness' | 'revenue' | 'recruitment')
 *  - amountUsd: number (USD)
 *  - actions: array of {title, description}
 */
const BonusGapActions = ({ component, amountUsd, actions = [], onGenerate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const goToActions = (preset) => {
    const params = new URLSearchParams();
    params.set('source', 'bonus_gap');
    if (component) params.set('component', component);
    if (preset?.title) params.set('title', preset.title);
    if (preset?.description) params.set('description', preset.description);
    navigate(`/tcn/me/actions?${params.toString()}`);
  };

  if (!component) return null;
  const accent = TCN.amber;

  return (
    <Card
      padded
      style={{
        padding: 16,
        background: `linear-gradient(135deg, ${accent}1a, ${TCN.surface1})`,
        border: `1px solid ${accent}33`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div
          style={{
            width: 36, height: 36, borderRadius: 10,
            background: `${accent}33`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name="target" size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11, fontWeight: 700,
              color: accent, letterSpacing: '0.08em',
              textTransform: 'uppercase', marginBottom: 4,
            }}
          >
            {t('tcn.m5.gap.label', 'Biggest payout gap')}
          </div>
          <div style={{ fontSize: 14, color: TCN.text, fontWeight: 600 }}>
            {t(`tcn.m5.bonus.${component}.label`, component)}
            {' '}
            <span style={{ color: TCN.textDim, fontWeight: 500 }}>
              ({formatUSD(amountUsd, { fractionDigits: amountUsd < 100 ? 2 : 0 })})
            </span>
          </div>
        </div>
      </div>

      {actions.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {actions.map((a, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: '10px 12px',
                background: TCN.surface2,
                border: `1px solid ${TCN.border}`,
                borderRadius: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }}>
                  {a.title}
                </div>
                {a.description && (
                  <div style={{ fontSize: 11, color: TCN.textDim, marginTop: 2 }}>
                    {a.description}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => goToActions(a)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${TCN.borderStrong}`,
                  color: TCN.cyan,
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('tcn.m5.gap.toAction', 'Add to log')}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <CTAButton onClick={() => goToActions(null)} variant="primary" size="sm" fullWidth>
          {t('tcn.m5.gap.cta', 'Translate into actions')}
        </CTAButton>
      )}

      {/* A5.7 — generate the full multi-action checklist from the pay-out gaps */}
      {onGenerate && (
        <div style={{ marginTop: 12 }}>
          <CTAButton
            onClick={onGenerate}
            variant="primary"
            size="md"
            fullWidth
            leading={<Icon name="sparkle" size={16} />}
          >
            {t('tcn.m5.gap.generate', "Generate today's actions")}
          </CTAButton>
        </div>
      )}
    </Card>
  );
};

export default BonusGapActions;
